import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Priority } from '@cortex/domain-org-issues';
import { PRIORITIES } from '@cortex/domain-org-issues/model';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ProtectedRoute } from '../components/auth/index.js';
import {
  BoardSettingsModal,
  CreateIssueModal,
  IssueDetailModal,
  KanbanBoard,
  LoadState,
} from '../components/features/org-issues/index.js';
import { useOrgIssues } from '../hooks/useOrgIssues.js';
import type { OrgIssueFilter } from '../lib/api/org-issues.js';
import { parseBoardSearch } from '../lib/org-issues.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードの看板。列（状態）ごとにカードを並べ（ピン留めが先頭・優先度のバッジ・担当・Next の進み）、動かせる課題はドラッグで列を移す。自分の担当・優先度で絞り、課題の起票・詳細（本文・Next・コメント・履歴）・ボードの設定を開く。開いている課題は URL に残す
 * @graph-connects useOrgIssues [calls] 課題の一覧
 * @graph-connects KanbanBoard [calls] 看板
 * @graph-connects IssueDetailModal [calls] 課題の詳細
 * @graph-connects CreateIssueModal [calls] 課題の起票
 * @graph-connects BoardSettingsModal [calls] ボードの設定
 */
export const Route = createFileRoute('/org-issues/$boardId')({
  component: OrgIssueBoardRoute,
  validateSearch: parseBoardSearch,
});

/** @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする */
function OrgIssueBoardRoute(): ReactNode {
  return (
    <ProtectedRoute pageKey="orgIssues">
      <OrgIssueBoardPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function OrgIssueBoardPage(): ReactNode {
  const { t } = useTranslation();
  const { boardId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [mineOnly, setMineOnly] = useState(false);
  const [priority, setPriority] = useState<Priority | ''>('');
  const [creating, setCreating] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const filter: OrgIssueFilter = {
    ...(mineOnly ? { assignee: 'me' } : {}),
    ...(priority === '' ? {} : { priority }),
  };
  const { data, isLoading, isError, error, refetch } = useOrgIssues(boardId, filter);

  const openIssue = (issueId: string | null) => {
    void navigate({ search: issueId === null ? {} : { issue: issueId }, replace: true });
  };

  return (
    <div className="space-y-4">
      <Link to="/org-issues" className="text-sm text-[var(--color-accent-primary)] hover:underline">
        ← {t('orgIssues.board.back')}
      </Link>
      <LoadState
        isLoading={isLoading}
        isError={isError}
        onRetry={() => {
          void refetch();
        }}
      />
      {/* 見られないボードは API が 404 で返す。その文言（ボードが見つかりません）をそのまま出す */}
      {isError && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {error.message}
        </p>
      )}
      {data !== undefined && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {data.board.board.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
                <Checkbox
                  checked={mineOnly}
                  onChange={(e) => {
                    setMineOnly(e.target.checked);
                  }}
                />
                {t('orgIssues.board.mineOnly')}
              </label>
              <Select
                aria-label={t('orgIssues.issue.priority')}
                size="sm"
                value={priority}
                options={[
                  { value: '', label: t('orgIssues.board.allPriorities') },
                  ...PRIORITIES.map((value) => ({
                    value,
                    label: t(`orgIssues.priority.${value}`),
                  })),
                ]}
                onChange={(e) => {
                  setPriority(PRIORITIES.find((value) => value === e.target.value) ?? '');
                }}
              />
              {data.board.permissions.canCreateIssue && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  {t('orgIssues.board.newIssue')}
                </Button>
              )}
              {data.board.permissions.canManage && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setConfiguring(true);
                  }}
                >
                  {t('orgIssues.board.settings')}
                </Button>
              )}
            </div>
          </div>
          <KanbanBoard
            board={data.board.board}
            issues={data.issues}
            people={data.people}
            onOpen={openIssue}
          />
          <IssueDetailModal
            board={data.board.board}
            issueId={search.issue ?? null}
            onClose={() => {
              openIssue(null);
            }}
          />
          {creating && (
            <CreateIssueModal
              board={data.board.board}
              open
              onClose={() => {
                setCreating(false);
              }}
              onCreated={(issueId) => {
                setCreating(false);
                openIssue(issueId);
              }}
            />
          )}
          {configuring && (
            <BoardSettingsModal
              board={data.board.board}
              people={data.people}
              open
              onClose={() => {
                setConfiguring(false);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
